import React, { useState, forwardRef, useEffect, createRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Scrollbars from 'react-custom-scrollbars-2';
import cn from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';

import { ExpandIcon } from 'components/common/icons';

import { convertToOptions } from 'helpers/SelectUtils';

import { COUNTRY_PHONE_CODES_WITH_COUNTRY, COUNTRY_PHONE_CODES } from 'components/common/listConstants';

import style from './PhoneInput.module.scss';
import inputStyle from './Input/Input.module.scss';
import styles from './Select/Select.module.scss';
import Flag from 'components/common/Flag/Flag';

const PhoneInput = ({
    containerRef,
    error,
    value,
    name,
    label,
    onChange,
    onChangeField,
    phoneCountryCode,
    phoneCountryCodeName,
    labelClassName,
    className,
    onBlur,
}) => {
    const { t } = useTranslation();
    const [phoneValue, setPhoneValue] = useState(value.replace(COUNTRY_PHONE_CODES[phoneCountryCode], ''));
    const [codeOptionsOpen, setCodeOptions] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState('');
    const searchInputRef = createRef();

    const handleCountryCodeSelect = (code) => {
        setCodeOptions(false);
        commitChangesToForm(code, phoneValue);
    };
    const handlePhoneInput = ({ target: { value } }) => {
        // eslint-disable-next-line no-useless-escape
        value = parseInt(value.replace(/[^0-9\.]/g, '')) || '';
        setPhoneValue(value);
        commitChangesToForm(phoneCountryCode, value);
    };
    const handleSearchInput = ({ target: { value } }) => {
        if (!value.match(/^([+\d]?\w?)*$/)) {
            return;
        }
        setSearchInputValue(value);
    };

    useEffect(() => {
        !phoneCountryCode && handleCountryCodeSelect('GB');
    }, []);

    useEffect(() => {
        searchInputRef.current && searchInputRef.current.focus();
    }, [codeOptionsOpen]);

    const commitChangesToForm = (countryCode, phone) => {
        const code = COUNTRY_PHONE_CODES[countryCode];
        const mergedValue = `${code || ''}${phone}`;
        if (onChangeField) {
            onChangeField({ target: { name: phoneCountryCodeName, value: countryCode } });
            onChangeField({ target: { name, value: mergedValue } });
        } else if (onChange) {
            onChange(name, mergedValue);
            onChange(phoneCountryCodeName, countryCode);
        }
    };
    const countryCode = useMemo(() => {
        if (!codeOptionsOpen && phoneCountryCode) {
            setSearchInputValue('');
            return (
                <div className={style.flagCode}>
                    <Flag country={phoneCountryCode} className={style.flag} />
                    {COUNTRY_PHONE_CODES[phoneCountryCode]}
                </div>
            );
        }

        searchInputRef.current && searchInputRef.current.focus();
        return (
            <input
                value={searchInputValue}
                onChange={handleSearchInput}
                ref={searchInputRef}
                className={styles.searchInput}
                placeholder={'+'}
            />
        );
    }, [codeOptionsOpen, searchInputValue, phoneCountryCode]);

    const sortByValue = (a, b) => {
        return a.value === 'GB' ? -1 : a.value === 'US' && b.value !== 'GB' ? -1 : 1;
    };
    const filterByLabel = (option) => {
        if (!searchInputValue) return true;
        const value = searchInputValue.toLowerCase().replace(/\+/g, '');

        const countryNameSearch = option.label
            .toLowerCase()
            .substr(0, value.length)
            .match(value);
        const countryCodeSearch = parseInt(option.label.match(/\d+/))
            .toString()
            .substr(0, value.length)
            .match(value);
        return countryNameSearch || countryCodeSearch;
    };
    const options = useMemo(
        () =>
            convertToOptions(COUNTRY_PHONE_CODES_WITH_COUNTRY(t))
                .filter(filterByLabel)
                .sort(sortByValue),
        [searchInputValue, t],
    );
    const inputContainer = cn(inputStyle.inputWrapper, className);
    return (
        <div className={style.wrapper}>
            <div className={inputContainer} ref={containerRef}>
                <div
                    className={cn(inputStyle.labelContainer, {
                        [inputStyle.errorLabel]: error,
                        [inputStyle.inputLabel]: error,
                    })}
                >
                    <label
                        className={cn(inputStyle.inputLabel, labelClassName, {
                            [inputStyle.filledLabel]: !!phoneValue,
                        })}
                    >
                        {label || t('profile:text.phone')}
                    </label>
                </div>

                <div className={style.fieldsWrapper}>
                    <div
                        className={style.select}
                        onClick={() => {
                            searchInputRef.current && searchInputRef.current.focus();
                            setCodeOptions(!codeOptionsOpen);
                        }}
                    >
                        {countryCode}
                        <ExpandIcon className={cn(style.expandIcon, { [style.open]: codeOptionsOpen })} />
                    </div>
                    {codeOptionsOpen && (
                        <div className={style.options}>
                            <OutsideClickHandler onOutsideClick={() => setCodeOptions(false)}>
                                <Scrollbars autoHeight autoHeightMin='2.75rem'>
                                    {options.map((option) => (
                                        <div
                                            key={option.value}
                                            className={style.option}
                                            onClick={() => handleCountryCodeSelect(option.value)}
                                        >
                                            <Flag country={option.value} />
                                            <span>{option.label}</span>
                                        </div>
                                    ))}
                                </Scrollbars>
                            </OutsideClickHandler>
                        </div>
                    )}
                    <input
                        className={cn(style.phoneInput, inputStyle.input)}
                        value={phoneValue}
                        onChange={handlePhoneInput}
                        placeholder={t('profile:text.enterPhone')}
                        onBlur={onBlur}
                    />
                </div>
                {error && <div className={cn(inputStyle.error, style.error)}>{error}</div>}
            </div>
        </div>
    );
};

PhoneInput.defaultProps = {
    name: 'phone',
    phoneCountryCodeName: 'phoneCountryCode',
};

export default forwardRef((props, ref) => <PhoneInput {...props} containerRef={ref} />);
